import { useState, useEffect, useRef, useCallback, CSSProperties } from 'react';
import { Helmet } from 'react-helmet';
import { HexColorPicker } from 'react-colorful';
import mokachikaLogo from '../assets/mokachika.svg';

interface DesignState {
  text: string;
  fontFamily: string;
  fontWeight: string;
  fontStyle: 'normal' | 'italic';
  fontSize: number;
  letterSpacing: number;
  color: string;
  textOpacity: number;
  backgroundColor: string;
  backgroundUseGradient: boolean;
  backgroundGradientStart: string;
  backgroundGradientEnd: string;
  backgroundGradientAngle: number;
  strokeColor: string;
  strokeWidth: number;
  rows: number;
  columns: number;
  rowSpacing: number;
  columnSpacing: number;
  spacingScaleX: number;
  spacingScaleY: number;
  rotation: number;
  rotationOffset: number;
  horizontalWave: number;
  verticalWave: number;
  gridWaveX: number;
  gridWaveY: number;
  textShadowEnabled: boolean;
  textShadowColor: string;
  textShadowBlur: number;
  textShadowOffsetX: number;
  textShadowOffsetY: number;
  glossEnabled: boolean;
  glossIntensity: number;
  glassEnabled: boolean;
  glassOpacity: number;
  glassHighlight: number;
  offsetX: number;
  offsetY: number;
  skewX: number;
  skewY: number;
  zoom: number;
  performanceMode: boolean;
  useTextCaching: boolean;
}

interface MouseState {
  isDragging: boolean;
  isRotating: boolean;
  isPanning: boolean;
  isAdjustingWave: boolean;
  isSkewing: boolean;
  startX: number;
  startY: number;
  startRotation: number;
  startOffsetX: number;
  startOffsetY: number;
  startHorizontalWave: number;
  startVerticalWave: number;
  startSkewX: number;
  startSkewY: number;
}

type MouseMode = 'move' | 'rotate' | 'wave' | 'skew';
type ColorPickerTarget = 'text' | 'background' | 'stroke' | 'backgroundGradientStart' | 'backgroundGradientEnd';
type TextEffect = 'none' | 'arch-up' | 'arch-down' | 'wave' | 'circle';
interface RenderOptions {
  displayWidth: number;
  displayHeight: number;
  dpr: number;
  useCaching?: boolean;
  performanceMode?: boolean;
  zoom?: number;
}

const COLOR_PRESETS = [
  { name: 'Mokachika', colors: ['#F97316', '#06B6D4', '#EA580C', '#FFF7ED'] },
  { name: 'Neon', colors: ['#ff006e', '#8338ec', '#3a86ff', '#06ffa5'] },
  { name: 'Sunset', colors: ['#ff6b35', '#f7931e', '#fdc500', '#c1121f'] },
  { name: 'Ocean', colors: ['#06ffa5', '#00d9ff', '#0077b6', '#023e8a'] },
  { name: 'Forest', colors: ['#52b788', '#2d6a4f', '#1b4332', '#081c15'] },
  { name: 'Purple', colors: ['#7209b7', '#560bad', '#3c096c', '#240046'] },
  { name: 'Fire', colors: ['#ff0a54', '#ff477e', '#ff5c8a', '#ff7096'] },
  { name: 'Mono', colors: ['#ffffff', '#adb5bd', '#6c757d', '#343a40'] },
];

const seoMetadata = {
  title: 'TextDesigner - Free Online Typography Tool with Effects & Export | mokachika',
  description: 'Free online text designer with advanced typography controls. Create stunning text designs with custom fonts, gradients, shadows, gloss & glass effects. Features grid layouts, rotation, skew, and wave effects. Export as high-quality PNG for logos, social media, and branding.',
  keywords: 'text design, typography tool, logo designer, free text editor, canvas editor, font designer, text effects, gradient text, text shadow, glass effect, gloss effect, custom fonts, online typography, text graphics, social media graphics, logo maker, branding tool, text art, typography generator, free design tool, mokachika',
  ogImage: mokachikaLogo,
  url: 'https://mokachika.com/text',
};

export const TextDesignerPage = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [controlsOpen, setControlsOpen] = useState(true);
  const [state, setState] = useState<DesignState>({
    text: 'YOUR TEXT',
    fontFamily: 'Inter',
    fontWeight: '400',
    fontStyle: 'normal',
    fontSize: 80,
    letterSpacing: 0,
    color: '#ffffff',
    textOpacity: 1,
    backgroundColor: '#1a1a2e',
    backgroundUseGradient: false,
    backgroundGradientStart: '#1a1a2e',
    backgroundGradientEnd: '#0f172a',
    backgroundGradientAngle: 45,
    strokeColor: '#F97316',
    strokeWidth: 0,
    rows: 1,
    columns: 1,
    rowSpacing: 100,
    columnSpacing: 100,
    spacingScaleX: 1,
    spacingScaleY: 1,
    rotation: 0,
    rotationOffset: 0,
    horizontalWave: 0,
    verticalWave: 0,
    gridWaveX: 0,
    gridWaveY: 0,
    textShadowEnabled: false,
    textShadowColor: '#000000',
    textShadowBlur: 0,
    textShadowOffsetX: 0,
    textShadowOffsetY: 0,
    glossEnabled: false,
    glossIntensity: 40,
    glassEnabled: false,
    glassOpacity: 0.4,
    glassHighlight: 0.6,
    offsetX: 0,
    offsetY: 0,
    skewX: 0,
    skewY: 0,
    zoom: 1,
    performanceMode: false,
    useTextCaching: false,
  });

  const [mouseState, setMouseState] = useState<MouseState>({
    isDragging: false,
    isRotating: false,
    isPanning: false,
    isAdjustingWave: false,
    isSkewing: false,
    startX: 0,
    startY: 0,
    startRotation: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    startHorizontalWave: 0,
    startVerticalWave: 0,
    startSkewX: 0,
    startSkewY: 0,
  });

  const [mouseMode, setMouseMode] = useState<MouseMode>('move');
  const [activeColorPicker, setActiveColorPicker] = useState<ColorPickerTarget | null>(null);
  const [customFontUrl, setCustomFontUrl] = useState('');
  const [customFonts, setCustomFonts] = useState<string[]>([]);
  const [customFontSources, setCustomFontSources] = useState<{ name: string; url: string }[]>([]);
  const [customFontMessage, setCustomFontMessage] = useState<{ text: string; tone: 'success' | 'error' | 'info' } | null>(null);
  const [showPerfInfo, setShowPerfInfo] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportWidth, setExportWidth] = useState(2048);
  const [exportHeight, setExportHeight] = useState(2048);
  const [exportDpr, setExportDpr] = useState(1);
  const [isExportingHd, setIsExportingHd] = useState(false);
  useEffect(() => {
    if (!customFontMessage) return;
    const timer = setTimeout(() => setCustomFontMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [customFontMessage]);
  const [presetsExpanded, setPresetsExpanded] = useState(false);
  const [presetTarget, setPresetTarget] = useState<'text' | 'background' | 'stroke'>('text');
  const [renderTrigger, setRenderTrigger] = useState(0);
  const [textEffect, setTextEffect] = useState<TextEffect>('none');
  const [effectIntensity, setEffectIntensity] = useState(50);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSnap, setGridSnap] = useState(false);
  const [gridSize, setGridSize] = useState(50);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [textImage, setTextImage] = useState<HTMLImageElement | null>(null);
  const [fontVariantAvailability, setFontVariantAvailability] = useState<Record<string, { normal: string[]; italic: string[] }>>({});
  const textCanvasCacheRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const measurementCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const FONT_WEIGHTS = [
    { label: 'Thin (100)', value: '100' },
    { label: 'Extra Light (200)', value: '200' },
    { label: 'Light (300)', value: '300' },
    { label: 'Regular (400)', value: '400' },
    { label: 'Medium (500)', value: '500' },
    { label: 'Semi Bold (600)', value: '600' },
    { label: 'Bold (700)', value: '700' },
    { label: 'Extra Bold (800)', value: '800' },
    { label: 'Black (900)', value: '900' },
  ];
  const ALL_WEIGHT_VALUES = FONT_WEIGHTS.map(weight => weight.value);
  const ALL_ITALIC_WEIGHT_PARAM = [
    ...ALL_WEIGHT_VALUES.map(value => `0,${value}`),
    ...ALL_WEIGHT_VALUES.map(value => `1,${value}`)
  ].join(';');
  const [effectPanelsOpen, setEffectPanelsOpen] = useState({
    shadow: false,
    gloss: false,
    glass: false,
  });

  const [availableFonts] = useState([
    'Inter', 'Roboto', 'Montserrat', 'Poppins', 'Playfair Display',
    'Bebas Neue', 'Oswald', 'Raleway', 'Lato', 'Open Sans',
    'Righteous', 'Permanent Marker', 'Lobster', 'Pacifico', 'Dancing Script'
  ]);

const getFormattedFontFamily = (fontName: string) => {
  const sanitized = fontName.replace(/"/g, '').trim();
  return sanitized.includes(' ') ? `"${sanitized}"` : sanitized;
};

interface ColorPickerFieldProps {
  label: string;
  color: string;
  pickerKey: ColorPickerTarget;
  onChange: (color: string) => void;
  containerStyle?: CSSProperties;
  labelStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
}

  const parseFontVariantsFromCss = (css: string) => {
    const mappings: Record<string, { normal: Set<string>; italic: Set<string> }> = {};
    const faceRegex = /@font-face\s*{([\s\S]*?)}/g;
    let match;
    while ((match = faceRegex.exec(css)) !== null) {
      const block = match[1];
      const familyMatch = block.match(/font-family:\s*['"]([^'"]+)['"]/);
      const weightMatch = block.match(/font-weight:\s*(\d+)/);
      const styleMatch = block.match(/font-style:\s*(italic|normal)/);
      if (!familyMatch || !weightMatch) continue;
      const family = familyMatch[1];
      const weight = weightMatch[1];
      const style = (styleMatch ? styleMatch[1] : 'normal') as 'normal' | 'italic';
      if (!mappings[family]) {
        mappings[family] = { normal: new Set(), italic: new Set() };
      }
      mappings[family][style].add(weight);
    }
    const result: Record<string, { normal: string[]; italic: string[] }> = {};
    Object.entries(mappings).forEach(([family, styles]) => {
      result[family] = {
        normal: Array.from(styles.normal).sort((a, b) => Number(a) - Number(b)),
        italic: Array.from(styles.italic).sort((a, b) => Number(a) - Number(b)),
      };
    });
    return result;
  };

  const mergeFontVariantAvailability = (css: string) => {
    if (!css) return;
    const parsed = parseFontVariantsFromCss(css);
    if (!Object.keys(parsed).length) return;
    setFontVariantAvailability(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(parsed).forEach(([family, styles]) => {
        const existing = next[family];
        const stylesChanged =
          !existing ||
          existing.normal.join(',') !== styles.normal.join(',') ||
          existing.italic.join(',') !== styles.italic.join(',');
        if (stylesChanged) {
          next[family] = styles;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const fetchAndStoreFontVariants = (url: string) => {
    if (!url) return;
    fetch(url)
      .then(res => (res.ok ? res.text() : ''))
      .then(css => {
        if (css) mergeFontVariantAvailability(css);
      })
      .catch(() => {});
  };

  const ensureWeightParamInCssUrl = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const families = parsedUrl.searchParams.getAll('family');
      if (families.length) {
        parsedUrl.searchParams.delete('family');
        families.forEach(family => {
          if (family.includes(':ital,wght@')) {
            parsedUrl.searchParams.append('family', family);
            return;
          }
          const baseName = family.split(':')[0];
          parsedUrl.searchParams.append('family', `${baseName}:ital,wght@${ALL_ITALIC_WEIGHT_PARAM}`);
        });
      }
      if (!parsedUrl.searchParams.has('display')) {
        parsedUrl.searchParams.set('display', 'swap');
      }
      return parsedUrl.toString();
    } catch {
      return url;
    }
  };

  const getWeightOptionsForFont = (fontFamily: string, fontStyle: 'normal' | 'italic') => {
    const variants = fontVariantAvailability[fontFamily];
    const weights = variants?.[fontStyle];
    if (weights && weights.length) {
      return FONT_WEIGHTS.filter(weight => weights.includes(weight.value));
    }
    return FONT_WEIGHTS;
  };

  const isFontStyleAvailable = (fontFamily: string, style: 'normal' | 'italic') => {
    const variants = fontVariantAvailability[fontFamily];
    if (!variants) return true;
    const stylesHaveData = (variants.normal?.length || 0) + (variants.italic?.length || 0);
    if (!stylesHaveData) return true;
    return Boolean(variants[style]?.length);
  };

  // Load Google Fonts dynamically
  useEffect(() => {
    const familyParams = availableFonts
      .map(font => `family=${encodeURIComponent(font).replace(/%20/g, '+')}:ital,wght@${ALL_ITALIC_WEIGHT_PARAM}`)
      .join('&');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    fetchAndStoreFontVariants(link.href);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (e.button === 2) e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const isRightClick = e.button === 2;

    if (isRightClick) {
      setMouseState({
        isDragging: false,
        isRotating: false,
        isPanning: true,
        isAdjustingWave: false,
        isSkewing: false,
        startX: x,
        startY: y,
        startRotation: state.rotation,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
        startHorizontalWave: state.horizontalWave,
        startVerticalWave: state.verticalWave,
        startSkewX: state.skewX,
        startSkewY: state.skewY,
      });
    } else if (mouseMode === 'move') {
      setMouseState({
        isDragging: true,
        isRotating: false,
        isPanning: false,
        isAdjustingWave: false,
        isSkewing: false,
        startX: x,
        startY: y,
        startRotation: state.rotation,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
        startHorizontalWave: state.horizontalWave,
        startVerticalWave: state.verticalWave,
        startSkewX: state.skewX,
        startSkewY: state.skewY,
      });
    } else if (mouseMode === 'rotate') {
      setMouseState({
        isDragging: false,
        isRotating: true,
        isPanning: false,
        isAdjustingWave: false,
        isSkewing: false,
        startX: x,
        startY: y,
        startRotation: state.rotation,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
        startHorizontalWave: state.horizontalWave,
        startVerticalWave: state.verticalWave,
        startSkewX: state.skewX,
        startSkewY: state.skewY,
      });
    } else if (mouseMode === 'wave') {
      setMouseState({
        isDragging: false,
        isRotating: false,
        isPanning: false,
        isAdjustingWave: true,
        isSkewing: false,
        startX: x,
        startY: y,
        startRotation: state.rotation,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
        startHorizontalWave: state.horizontalWave,
        startVerticalWave: state.verticalWave,
        startSkewX: state.skewX,
        startSkewY: state.skewY,
      });
    } else if (mouseMode === 'skew') {
      setMouseState({
        isDragging: false,
        isRotating: false,
        isPanning: false,
        isAdjustingWave: false,
        isSkewing: true,
        startX: x,
        startY: y,
        startRotation: state.rotation,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
        startHorizontalWave: state.horizontalWave,
        startVerticalWave: state.verticalWave,
        startSkewX: state.skewX,
        startSkewY: state.skewY,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || (!mouseState.isDragging && !mouseState.isRotating && !mouseState.isPanning && !mouseState.isAdjustingWave && !mouseState.isSkewing)) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (mouseState.isPanning) {
      let dx = x - mouseState.startX;
      let dy = y - mouseState.startY;
      if (gridSnap) {
        dx = Math.round(dx / gridSize) * gridSize;
        dy = Math.round(dy / gridSize) * gridSize;
      }
      setState(prev => ({
        ...prev,
        offsetX: mouseState.startOffsetX + dx,
        offsetY: mouseState.startOffsetY + dy,
      }));
    } else if (mouseState.isRotating) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const angle = Math.atan2(y - centerY, x - centerX) - Math.atan2(mouseState.startY - centerY, mouseState.startX - centerX);
      const degrees = angle * (180 / Math.PI);
      setState(prev => ({ ...prev, rotation: mouseState.startRotation + degrees }));
    } else if (mouseState.isDragging) {
      let dx = x - mouseState.startX;
      let dy = y - mouseState.startY;
      if (gridSnap) {
        dx = Math.round(dx / gridSize) * gridSize;
        dy = Math.round(dy / gridSize) * gridSize;
      }
      setState(prev => ({
        ...prev,
        offsetX: mouseState.startOffsetX + dx,
        offsetY: mouseState.startOffsetY + dy,
      }));
    } else if (mouseState.isAdjustingWave) {
      const dx = (x - mouseState.startX) / 100;
      const dy = (y - mouseState.startY) / 100;
      setState(prev => ({
        ...prev,
        horizontalWave: Math.max(0, Math.min(30, mouseState.startHorizontalWave + dx)),
        verticalWave: Math.max(0, Math.min(30, mouseState.startVerticalWave + dy)),
      }));
    } else if (mouseState.isSkewing) {
      const dx = (x - mouseState.startX) / 5;
      const dy = (y - mouseState.startY) / 5;
      setState(prev => ({
        ...prev,
        skewX: Math.max(-100, Math.min(100, mouseState.startSkewX + dx)),
        skewY: Math.max(-100, Math.min(100, mouseState.startSkewY + dy)),
      }));
    }
  };

  const handleMouseUp = () => {
    setMouseState(prev => ({ ...prev, isDragging: false, isRotating: false, isPanning: false, isAdjustingWave: false, isSkewing: false }));
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
    setState(prev => ({
      ...prev,
      zoom: Math.max(0.1, Math.min(5, prev.zoom + zoomDelta)),
    }));
  };

  // Load custom Google Font
  const registerCustomFont = (fontName: string, cssUrl: string, { skipAlert } = { skipAlert: false }) => {
    if ([...availableFonts, ...customFonts].includes(fontName)) {
      if (!skipAlert) {
        setCustomFontMessage({ text: `Font "${fontName}" is already available.`, tone: 'info' });
      }
      return false;
    }
    const link = document.createElement('link');
    link.href = cssUrl;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    fetchAndStoreFontVariants(cssUrl);
    setCustomFonts(prev => [...prev, fontName]);
    setCustomFontSources(prev => [...prev, { name: fontName, url: cssUrl }]);
    return true;
  };

  const loadCustomFont = () => {
    if (!customFontUrl) return;

    let cssUrl = customFontUrl;
    let fontName = '';

    if (customFontUrl.includes('/specimen/')) {
      const specMatch = customFontUrl.match(/\/specimen\/([^?#]+)/);
      if (specMatch) {
        fontName = specMatch[1].replace(/\+/g, ' ').trim();
        cssUrl = `https://fonts.googleapis.com/css2?family=${specMatch[1]}:ital,wght@${ALL_ITALIC_WEIGHT_PARAM}&display=swap`;
      }
    } else {
      const cssMatch = customFontUrl.match(/family=([^&:]+)/);
      if (cssMatch) {
        fontName = cssMatch[1].replace(/\+/g, ' ');
      }
      cssUrl = ensureWeightParamInCssUrl(customFontUrl);
    }

    if (!fontName) {
      alert('Invalid URL. Please paste a Google Fonts URL from the specimen page or CSS link.');
      return;
    }

    const added = registerCustomFont(fontName, cssUrl);
    if (!added) {
      updateState({ fontFamily: fontName });
      setCustomFontUrl('');
      return;
    }
    updateState({ fontFamily: fontName });
    setCustomFontUrl('');
    setCustomFontMessage({ text: `Font "${fontName}" loaded and applied.`, tone: 'success' });
  };

  // Force re-render when font changes
  useEffect(() => {
    const loadFont = async () => {
      try {
        await document.fonts.load(`${state.fontStyle} ${state.fontWeight} ${state.fontSize}px ${getFormattedFontFamily(state.fontFamily)}`);
        setRenderTrigger(prev => prev + 1);
      } catch (e) {
        setRenderTrigger(prev => prev + 1);
      }
    };
    loadFont();
  }, [state.fontFamily, state.fontSize, state.fontWeight, state.fontStyle]);

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = window.innerWidth;
    const displayHeight = window.innerHeight - 100;
    canvas.width = Math.floor(displayWidth * dpr);
    canvas.height = Math.floor(displayHeight * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // Draw background
    if (backgroundImage) {
      ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
    } else if (state.backgroundUseGradient) {
      const angleRad = (state.backgroundGradientAngle % 360) * Math.PI / 180;
      const halfDiagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2) / 2;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const dx = Math.cos(angleRad) * halfDiagonal;
      const dy = Math.sin(angleRad) * halfDiagonal;
      const gradient = ctx.createLinearGradient(centerX - dx, centerY - dy, centerX + dx, centerY + dy);
      gradient.addColorStop(0, state.backgroundGradientStart);
      gradient.addColorStop(1, state.backgroundGradientEnd);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = state.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Draw grid if enabled
    if (showGrid && gridSize >= 20) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      const maxIterations = 1000;
      let iterations = 0;
      for (let x = 0; x <= canvas.width && iterations < maxIterations; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
        iterations++;
      }
      iterations = 0;
      for (let y = 0; y <= canvas.height && iterations < maxIterations; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
        iterations++;
      }
      ctx.restore();
    }

    ctx.save();
    ctx.translate(displayWidth / 2, displayHeight / 2);
    ctx.scale(state.zoom, state.zoom);
    ctx.translate(-displayWidth / 2, -displayHeight / 2);

    const shouldApplyTextWave = state.horizontalWave > 0 || state.verticalWave > 0;
    const waveAmplitudeUnit = state.fontSize * 0.05;
    const getTextWaveOffset = (index: number, total: number) => {
      if (!shouldApplyTextWave) return { x: 0, y: 0 };
      const progress = total > 1 ? index / (total - 1) : 0;
      const angle = progress * Math.PI * 2;
      return {
        x: state.horizontalWave * waveAmplitudeUnit * Math.sin(angle),
        y: state.verticalWave * waveAmplitudeUnit * Math.cos(angle),
      };
    };

    const renderStyledContent = (
      targetCtx: CanvasRenderingContext2D,
      content: string,
      options?: { skipHeavyEffects?: boolean }
    ) => {
      const skipHeavy = options?.skipHeavyEffects;

      if (state.strokeWidth > 0) {
        targetCtx.strokeStyle = state.strokeColor;
        targetCtx.lineWidth = state.strokeWidth;
        targetCtx.strokeText(content, 0, 0);
      }

      const textOpacity = Math.min(1, Math.max(0, state.textOpacity));
      targetCtx.save();
      targetCtx.globalAlpha = textOpacity;
      if (textImage) {
        const pattern = targetCtx.createPattern(textImage, 'repeat');
        if (pattern) targetCtx.fillStyle = pattern;
        else targetCtx.fillStyle = state.color;
      } else {
        targetCtx.fillStyle = state.color;
      }
      targetCtx.fillText(content, 0, 0);
      targetCtx.restore();

      const applyGloss = state.glossEnabled && !skipHeavy;
      if (applyGloss) {
        targetCtx.save();
        targetCtx.globalCompositeOperation = 'lighter';
        targetCtx.globalAlpha = state.glossIntensity / 100;
        const glossGradient = targetCtx.createLinearGradient(0, -state.fontSize, 0, state.fontSize);
        glossGradient.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
        glossGradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.2)');
        glossGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        targetCtx.fillStyle = glossGradient;
        targetCtx.fillText(content, 0, 0);
        targetCtx.restore();
      }

      const applyGlass = state.glassEnabled && !skipHeavy;
      if (applyGlass) {
        targetCtx.save();
        const glassOpacity = Math.min(1, Math.max(0, state.glassOpacity));
        const glassHighlight = Math.min(1, Math.max(0, state.glassHighlight));
        targetCtx.globalCompositeOperation = 'lighter';
        targetCtx.globalAlpha = glassOpacity * 0.6;
        const glassGradient = targetCtx.createLinearGradient(0, -state.fontSize, 0, state.fontSize);
        glassGradient.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
        glassGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.05)');
        glassGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        targetCtx.fillStyle = glassGradient;
        targetCtx.fillText(content, 0, 0);

        targetCtx.globalAlpha = glassHighlight * 0.8;
        targetCtx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        targetCtx.lineWidth = Math.max(1, state.fontSize * 0.02);
        targetCtx.strokeText(content, 0, 0);
        targetCtx.restore();
      }
    };

    const drawTextWithEffect = (
      text: string,
      x: number,
      y: number,
      rotation: number,
      options?: { skipHeavyEffects?: boolean },
      ctxOverride?: CanvasRenderingContext2D
    ) => {
      const targetCtx = ctxOverride ?? ctx;
      const skipHeavy = options?.skipHeavyEffects;
      targetCtx.save();
      targetCtx.translate(x, y);
      targetCtx.rotate(rotation);

      if (state.skewX !== 0 || state.skewY !== 0) {
        targetCtx.transform(1, state.skewY * 0.01, state.skewX * 0.01, 1, 0, 0);
      }

      const fontWeight = state.fontWeight || '400';
      const fontStyle = state.fontStyle || 'normal';
      const fontFamilyStack = `${getFormattedFontFamily(state.fontFamily)}, sans-serif`;
      targetCtx.font = `${fontStyle} ${fontWeight} ${state.fontSize}px ${fontFamilyStack}`;
      targetCtx.textAlign = 'center';
      targetCtx.textBaseline = 'middle';
      if (state.textShadowEnabled && !skipHeavy) {
        targetCtx.shadowColor = state.textShadowColor;
        targetCtx.shadowBlur = state.textShadowBlur;
        targetCtx.shadowOffsetX = state.textShadowOffsetX;
        targetCtx.shadowOffsetY = state.textShadowOffsetY;
      } else {
        targetCtx.shadowColor = 'transparent';
        targetCtx.shadowBlur = 0;
        targetCtx.shadowOffsetX = 0;
        targetCtx.shadowOffsetY = 0;
      }

      if (textEffect === 'none') {
        if (state.letterSpacing !== 0 || shouldApplyTextWave) {
          const chars = text.split('');
          const charWidths = chars.map(char => targetCtx.measureText(char).width);
          const totalWidth = charWidths.reduce((sum, width) => sum + width, 0);
          const totalSpacing = (chars.length - 1) * state.letterSpacing;
          let textX = -(totalWidth + totalSpacing) / 2;

          chars.forEach((char, index) => {
            const charWidth = charWidths[index];
            const { x: waveX, y: waveY } = getTextWaveOffset(index, chars.length);
            const drawX = textX + charWidth / 2 + waveX;
            const drawY = waveY;

            targetCtx.save();
            targetCtx.translate(drawX, drawY);
            renderStyledContent(targetCtx, char, { skipHeavyEffects: skipHeavy });
            targetCtx.restore();
            textX += charWidth + state.letterSpacing;
          });
        } else {
          renderStyledContent(targetCtx, text, { skipHeavyEffects: skipHeavy });
        }
      } else {
        const chars = text.split('');
        const charWidths = chars.map(char => targetCtx.measureText(char).width + state.letterSpacing);
        const totalWidth = charWidths.reduce((sum, w) => sum + w, 0);
        const radius = effectIntensity * 2;

        let currentX = -totalWidth / 2;

        chars.forEach((char, i) => {
          const charWidth = charWidths[i];
          const progress = (currentX + charWidth / 2) / totalWidth;

          let offsetY = 0;
          let charRotation = 0;

          if (textEffect === 'arch-up') {
            offsetY = -Math.pow(progress, 2) * radius;
            charRotation = progress * 0.5;
          } else if (textEffect === 'arch-down') {
            offsetY = Math.pow(progress, 2) * radius;
            charRotation = -progress * 0.5;
          } else if (textEffect === 'wave') {
            offsetY = Math.sin(progress * Math.PI * 4) * (radius / 2);
            charRotation = Math.cos(progress * Math.PI * 4) * 0.3;
          } else if (textEffect === 'circle') {
            const angle = progress * Math.PI;
            offsetY = -Math.cos(angle) * radius;
            charRotation = angle;
          }

          targetCtx.save();
          const { x: charWaveX, y: charWaveY } = getTextWaveOffset(i, chars.length);
          targetCtx.translate(currentX + charWidth / 2 + charWaveX, offsetY + charWaveY);
          targetCtx.rotate(charRotation);

          renderStyledContent(targetCtx, char, { skipHeavyEffects: skipHeavy });

          targetCtx.restore();
          currentX += charWidth;
        });
      }

      targetCtx.restore();
    };

    if (!measurementCanvasRef.current) {
      measurementCanvasRef.current = document.createElement('canvas');
    }
    const measurementCtx = measurementCanvasRef.current.getContext('2d');
    const textImageSrc = textImage ? textImage.src : null;

    const getCachedTextCanvas = () => {
      if (!state.useTextCaching || state.performanceMode || !measurementCtx || !state.text) return null;
      measurementCtx.font = `${state.fontStyle} ${state.fontWeight} ${state.fontSize}px ${getFormattedFontFamily(state.fontFamily)}`;
      const metrics = measurementCtx.measureText(state.text);
      const padding = Math.max(40, state.fontSize);
      const extraSpacing = state.letterSpacing > 0 ? (Math.max(0, state.text.length - 1) * state.letterSpacing) : 0;
      const width = Math.max(1, Math.ceil(metrics.width + padding + extraSpacing));
      const height = Math.max(1, Math.ceil(state.fontSize * 2 + padding));
      const dpr = window.devicePixelRatio || 1;
      const scaledWidth = Math.ceil(width * dpr);
      const scaledHeight = Math.ceil(height * dpr);
      const cacheKey = JSON.stringify({
        text: state.text,
        fontFamily: state.fontFamily,
        fontWeight: state.fontWeight,
        fontStyle: state.fontStyle,
        fontSize: state.fontSize,
        letterSpacing: state.letterSpacing,
        color: state.color,
        textOpacity: state.textOpacity,
        strokeColor: state.strokeColor,
        strokeWidth: state.strokeWidth,
        textEffect,
        effectIntensity,
        textShadowEnabled: state.textShadowEnabled,
        textShadowColor: state.textShadowColor,
        textShadowBlur: state.textShadowBlur,
        textShadowOffsetX: state.textShadowOffsetX,
        textShadowOffsetY: state.textShadowOffsetY,
        glossEnabled: state.glossEnabled,
        glossIntensity: state.glossIntensity,
        glassEnabled: state.glassEnabled,
        glassOpacity: state.glassOpacity,
        glassHighlight: state.glassHighlight,
        textImageSrc,
        horizontalWave: state.horizontalWave,
        verticalWave: state.verticalWave,
        renderTrigger
      });
      const cached = textCanvasCacheRef.current.get(cacheKey);
      if (cached) return cached;
      const offscreen = document.createElement('canvas');
      offscreen.width = scaledWidth;
      offscreen.height = scaledHeight;
      const offCtx = offscreen.getContext('2d');
      if (!offCtx) return null;
      offCtx.scale(dpr, dpr);
      drawTextWithEffect(state.text, width / 2, height / 2, 0, undefined, offCtx);
      textCanvasCacheRef.current.set(cacheKey, offscreen);
      if (textCanvasCacheRef.current.size > 12) {
        const firstKey = textCanvasCacheRef.current.keys().next().value;
        textCanvasCacheRef.current.delete(firstKey);
      }
      return offscreen;
    };

    const cachedTextCanvas = getCachedTextCanvas();

    const centerX = canvas.width / 2 + state.offsetX;
    const centerY = canvas.height / 2 + state.offsetY;

    const scaledRowSpacing = state.rowSpacing * state.spacingScaleY;
    const scaledColumnSpacing = state.columnSpacing * state.spacingScaleX;

    const totalWidth = (state.columns - 1) * scaledColumnSpacing;
    const totalHeight = (state.rows - 1) * scaledRowSpacing;
    const startX = centerX - totalWidth / 2;
    const startY = centerY - totalHeight / 2;

    const hasRenderableText = state.text.trim().length > 0;
    if (!hasRenderableText) {
      ctx.restore();
      return;
    }

    for (let row = 0; row < state.rows; row++) {
      for (let col = 0; col < state.columns; col++) {
        const index = row * state.columns + col;

        const colProgress = state.columns > 1 ? col / (state.columns - 1) : 0.5;
        const rowProgress = state.rows > 1 ? row / (state.rows - 1) : 0.5;
        const gridWaveAmplitudeX = state.gridWaveX * 80;
        const gridWaveAmplitudeY = state.gridWaveY * 80;
        const waveOffsetX = gridWaveAmplitudeX === 0 ? 0 : Math.sin(colProgress * Math.PI * 4) * gridWaveAmplitudeX;
        const waveOffsetY = gridWaveAmplitudeY === 0 ? 0 : Math.sin(rowProgress * Math.PI * 4) * gridWaveAmplitudeY;

        const x = startX + col * scaledColumnSpacing + waveOffsetX;
        const y = startY + row * scaledRowSpacing + waveOffsetY;
        const rotation = (state.rotation + index * state.rotationOffset) * (Math.PI / 180);

        if (cachedTextCanvas) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(rotation);
          if (state.skewX !== 0 || state.skewY !== 0) {
            ctx.transform(1, state.skewY * 0.01, state.skewX * 0.01, 1, 0, 0);
          }
          ctx.drawImage(cachedTextCanvas, -cachedTextCanvas.width / 2, -cachedTextCanvas.height / 2);
          ctx.restore();
        } else {
          drawTextWithEffect(state.text, x, y, rotation, { skipHeavyEffects: state.performanceMode });
        }
      }
    }

    ctx.restore();
  }, [state, renderTrigger, textEffect, effectIntensity, showGrid, gridSize, backgroundImage, textImage]);

  const updateState = (updates: Partial<DesignState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    const variants = fontVariantAvailability[state.fontFamily];
    if (!variants) return;
    const normalWeights = variants.normal || [];
    const italicWeights = variants.italic || [];

    let nextStyle: 'normal' | 'italic' = state.fontStyle;
    if (nextStyle === 'italic' && !italicWeights.length && normalWeights.length) {
      nextStyle = 'normal';
    } else if (nextStyle === 'normal' && !normalWeights.length && italicWeights.length) {
      nextStyle = 'italic';
    }

    const styleWeights = nextStyle === 'italic' ? italicWeights : normalWeights;
    let nextWeight = state.fontWeight;
    if (styleWeights.length && !styleWeights.includes(nextWeight)) {
      nextWeight = styleWeights.includes('400') ? '400' : styleWeights[0];
    }

    if (nextStyle !== state.fontStyle || nextWeight !== state.fontWeight) {
      setState(prev => ({ ...prev, fontStyle: nextStyle, fontWeight: nextWeight }));
    }
  }, [state.fontFamily, state.fontStyle, state.fontWeight, fontVariantAvailability]);

  const toggleEffectPanel = (panel: keyof typeof effectPanelsOpen) => {
    setEffectPanelsOpen(prev => ({ ...prev, [panel]: !prev[panel] }));
  };

  const exportImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `text-design-${Date.now()}.png`;
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleBackgroundImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setBackgroundImage(img);
        setRenderTrigger(prev => prev + 1);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleTextImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setTextImage(img);
        setRenderTrigger(prev => prev + 1);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const CONFIG_VERSION = 2;

  const getConfigPayload = () => ({
    version: CONFIG_VERSION,
    design: state,
    customFonts: customFontSources,
    meta: {
      exportedAt: new Date().toISOString()
    }
  });

  const exportConfig = () => {
    const payload = getConfigPayload();
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `text-designer-config-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const applyConfig = (config: unknown) => {
    if (!config || typeof config !== 'object') {
      alert('Invalid configuration file.');
      return;
    }
    const { version, design, customFonts: configFonts } = config as {
      version?: number;
      design?: Partial<DesignState>;
      customFonts?: Array<{ name?: string; url?: string }>;
    };
    if (typeof version !== 'number' || !design || typeof design !== 'object') {
      alert('Invalid configuration structure.');
      return;
    }
    const validKeys = new Set(Object.keys(state));
    const sanitized: Partial<DesignState> = {};
    Object.entries(design).forEach(([key, value]) => {
      if (validKeys.has(key)) {
        (sanitized as any)[key] = value;
      }
    });
    if (!Object.keys(sanitized).length) {
      alert('No valid settings found in configuration.');
      return;
    }
    setState(prev => ({ ...prev, ...sanitized }));

    if (Array.isArray(configFonts)) {
      configFonts.forEach(font => {
        if (!font || typeof font.name !== 'string' || typeof font.url !== 'string') return;
        registerCustomFont(font.name, font.url, { skipAlert: true });
      });
    }

    setCustomFontMessage({ text: 'Configuration applied.', tone: 'success' });
  };

  const handleConfigUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        if (typeof text === 'string') {
          const parsed = JSON.parse(text);
          applyConfig(parsed);
        }
      } catch {
        alert('Failed to parse configuration file.');
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const ColorPickerField = ({
    label,
    color,
    pickerKey,
    onChange,
    containerStyle,
    labelStyle,
    buttonStyle
  }: ColorPickerFieldProps) => {
    const baseContainer: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.4rem' };
    const baseLabel: CSSProperties = { color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 };
    const baseButton: CSSProperties = {
      width: '100%',
      padding: '0.5rem',
      backgroundColor: color,
      border: '1px solid rgba(249, 115, 22, 0.3)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.75rem',
      fontWeight: 500,
      cursor: 'pointer',
      boxSizing: 'border-box'
    };

    return (
      <div style={{ ...baseContainer, ...(containerStyle || {}) }}>
        <label style={{ ...baseLabel, ...(labelStyle || {}) }}>{label}</label>
        <button
          onClick={() => setActiveColorPicker(prev => (prev === pickerKey ? null : pickerKey))}
          style={{ ...baseButton, ...(buttonStyle || {}) }}
        >
          {color}
        </button>
        {activeColorPicker === pickerKey && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.75rem',
            background: 'rgba(31, 31, 46, 0.95)',
            border: '1px solid rgba(249, 115, 22, 0.3)',
            borderRadius: '8px'
          }}>
            <HexColorPicker color={color} onChange={onChange} />
          </div>
        )}
      </div>
    );
  };

  const italicAvailable = isFontStyleAvailable(state.fontFamily, 'italic');

  return (
    <>
      <Helmet>
        <title>{seoMetadata.title}</title>
        <meta name="description" content={seoMetadata.description} />
        <meta name="keywords" content={seoMetadata.keywords} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoMetadata.url} />
        <meta property="og:title" content={seoMetadata.title} />
        <meta property="og:description" content={seoMetadata.description} />
        <meta property="og:image" content={seoMetadata.ogImage} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoMetadata.url} />
      </Helmet>

      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#1a1a2e',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <header style={{
          height: '50px',
          background: '#1f1f2e',
          borderBottom: '1px solid rgba(249, 115, 22, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px'
          }}>
            <a
              href="/"
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: '18px',
                color: '#A8A29E',
                textDecoration: 'none',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#D6D3D1'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#A8A29E'}
            >
              mokachika
            </a>
            <span style={{ color: '#57534E', fontSize: '16px' }}>|</span>
            <span style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: '18px',
              color: '#E5E7EB'
            }}>
              TextDesigner
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={exportImage} style={{
              padding: '0.45rem 1.25rem',
              background: 'linear-gradient(135deg, #F97316, #EA580C)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
            }}>
              Export PNG
            </button>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                onClick={exportConfig}
                style={{
                  padding: '0.45rem 0.9rem',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#E5E7EB',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                title="Download design settings JSON"
              >
                Export Settings
              </button>
              <label style={{
                padding: '0.45rem 0.9rem',
                background: 'rgba(255,255,255,0.08)',
                color: '#E5E7EB',
                border: '1px dashed rgba(255,255,255,0.3)',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}>
                Import Settings
                <input
                  type="file"
                  accept="application/json"
                  onChange={handleConfigUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        </header>

        {/* Toggle Controls Button */}
        <button
          onClick={() => setControlsOpen(!controlsOpen)}
          style={{
            position: 'fixed',
            top: '108px',
            right: '18px',
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#1a1a2e',
            border: '1px solid rgba(249, 115, 22, 0.3)',
            fontSize: '1.25rem',
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={controlsOpen ? 'Hide Controls' : 'Show Controls'}
        >
          {controlsOpen ? '✕' : '⚙️'}
        </button>

        {/* Mode Toolbar */}
        <div style={{
          position: 'fixed',
          top: '50px',
          left: 0,
          right: 0,
          height: '48px',
          background: '#1f1f2e',
          borderBottom: '1px solid rgba(249, 115, 22, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          padding: '0 1rem',
          zIndex: 1200,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {(['move', 'rotate', 'wave', 'skew'] as MouseMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setMouseMode(mode)}
                style={{
                  padding: '0.4rem 0.875rem',
                  background: mouseMode === mode ? 'linear-gradient(135deg, #F97316, #EA580C)' : 'transparent',
                  border: mouseMode === mode ? '1px solid #F97316' : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: mouseMode === mode ? 'white' : '#9ca3af',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap'
                }}
              >
                {mode}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ color: '#6b7280', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
              Right-click to pan • Scroll to zoom
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#e5e7eb', fontSize: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={state.performanceMode}
                  onChange={(e) => updateState({ performanceMode: e.target.checked })}
                  style={{ accentColor: '#F97316' }}
                />
                Perf Mode
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#e5e7eb', fontSize: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={state.useTextCaching}
                  onChange={(e) => updateState({ useTextCaching: e.target.checked })}
                  style={{ accentColor: '#F97316' }}
                />
                Cache
              </label>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowPerfInfo(prev => !prev)}
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'transparent',
                    color: '#e5e7eb',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                  title="About performance options"
                >
                  i
                </button>
                {showPerfInfo && (
                  <div style={{
                    position: 'absolute',
                    top: '32px',
                    right: 0,
                    width: '220px',
                    padding: '0.6rem',
                    background: 'rgba(31, 31, 46, 0.95)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#e5e7eb',
                    fontSize: '0.75rem',
                    zIndex: 1500,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.4)'
                  }}>
                    <strong style={{ color: '#F97316' }}>Performance Mode</strong>: Skips shadows, gloss, glass during edits.<br /><br />
                    <strong style={{ color: '#06B6D4' }}>Cache</strong>: Reuses rendered text as bitmaps. Turn off for pure vector draws.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Area */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onContextMenu={handleContextMenu}
            style={{
              flex: 1,
              cursor: mouseState.isPanning ? 'grabbing' :
                      mouseState.isRotating ? 'grabbing' :
                      mouseState.isDragging ? 'move' :
                      mouseState.isAdjustingWave ? 'crosshair' :
                      mouseState.isSkewing ? 'nwse-resize' :
                      mouseMode === 'move' ? 'move' :
                      mouseMode === 'rotate' ? 'grab' :
                      mouseMode === 'wave' ? 'crosshair' :
                      mouseMode === 'skew' ? 'nwse-resize' :
                      'default'
            }}
          />

          {/* Controls Panel */}
          <div style={{
            position: 'fixed',
            top: '98px',
            right: 0,
            width: '400px',
            height: 'calc(100vh - 98px)',
            background: 'rgba(26, 26, 46, 0.98)',
            backdropFilter: 'blur(10px)',
            borderLeft: '1px solid rgba(249, 115, 22, 0.2)',
            padding: '1rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            transition: 'transform 0.3s ease',
            transform: controlsOpen ? 'translateX(0)' : 'translateX(100%)',
            zIndex: 999,
            boxShadow: controlsOpen ? '-4px 0 24px rgba(0, 0, 0, 0.3)' : 'none'
          }}>
            {/* Text Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text</label>
              <input
                type="text"
                value={state.text}
                onChange={(e) => updateState({ text: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.625rem',
                  background: 'rgba(31, 31, 46, 0.8)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Font Selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Font</label>
              <select
                value={state.fontFamily}
                onChange={(e) => updateState({ fontFamily: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.625rem',
                  background: 'rgba(31, 31, 46, 0.8)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontFamily: state.fontFamily,
                  boxSizing: 'border-box'
                }}
              >
                {availableFonts.map(font => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </option>
                ))}
                {customFonts.length > 0 && <option disabled>── Custom Fonts ──</option>}
                {customFonts.map(font => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Font Weight</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: italicAvailable ? '#e5e7eb' : '#6b7280', fontSize: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={state.fontStyle === 'italic'}
                    onChange={(e) => updateState({ fontStyle: e.target.checked ? 'italic' : 'normal' })}
                    disabled={!italicAvailable}
                  />
                  Italic
                </label>
              </div>
              <select
                value={state.fontWeight}
                onChange={(e) => updateState({ fontWeight: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.625rem',
                  background: 'rgba(31, 31, 46, 0.8)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              >
                {getWeightOptionsForFont(state.fontFamily, state.fontStyle).map(weight => (
                  <option key={weight.value} value={weight.value}>
                    {weight.label}
                  </option>
                ))}
              </select>
              {!italicAvailable && (
                <small style={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                  Italic not available for this font
                </small>
              )}
            </div>

            {/* Load Custom Font */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Load Custom Font</label>
              <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                <input
                  type="text"
                  value={customFontUrl}
                  onChange={(e) => setCustomFontUrl(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadCustomFont()}
                  placeholder="https://fonts.google.com/specimen/Font+Name"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0.5rem 0.625rem',
                    background: 'rgba(31, 31, 46, 0.8)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button onClick={loadCustomFont} style={{
                  flexShrink: 0,
                  padding: '0.5rem 0.875rem',
                  background: 'linear-gradient(135deg, #06B6D4, #0891B2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}>
                  Load
                </button>
              </div>
              <small style={{ color: 'rgba(229, 231, 235, 0.5)', fontSize: '0.7rem' }}>
                Paste Google Fonts URL or CSS link
              </small>
              {customFontMessage && (
                <div style={{
                  marginTop: '0.35rem',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: customFontMessage.tone === 'success' ? '#BBF7D0' :
                         customFontMessage.tone === 'error' ? '#FCA5A5' : '#E5E7EB',
                  background: customFontMessage.tone === 'success'
                    ? 'rgba(34,197,94,0.15)'
                    : customFontMessage.tone === 'error'
                    ? 'rgba(239,68,68,0.15)'
                    : 'rgba(255,255,255,0.05)',
                  border: customFontMessage.tone === 'success'
                    ? '1px solid rgba(34,197,94,0.4)'
                    : customFontMessage.tone === 'error'
                    ? '1px solid rgba(239,68,68,0.4)'
                    : '1px solid rgba(255,255,255,0.1)'
                }}>
                  {customFontMessage.text}
                </div>
              )}
            </div>

            {/* Size & Spacing */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Font Size</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="20"
                    max="500"
                    value={state.fontSize}
                    onChange={(e) => updateState({ fontSize: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ fontSize: 80 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="20"
                    max="500"
                    value={state.fontSize}
                    onChange={(e) => updateState({ fontSize: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Letter Spacing</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="-10"
                    max="100"
                    value={state.letterSpacing}
                    onChange={(e) => updateState({ letterSpacing: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ letterSpacing: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="-10"
                    max="100"
                    value={state.letterSpacing}
                    onChange={(e) => updateState({ letterSpacing: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Rows</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={state.rows}
                    onChange={(e) => updateState({ rows: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ rows: 1 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={state.rows}
                    onChange={(e) => updateState({ rows: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Columns</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={state.columns}
                    onChange={(e) => updateState({ columns: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ columns: 1 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={state.columns}
                    onChange={(e) => updateState({ columns: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Row Spacing</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={state.rowSpacing}
                    onChange={(e) => updateState({ rowSpacing: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ rowSpacing: 100 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={state.rowSpacing}
                    onChange={(e) => updateState({ rowSpacing: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Column Spacing</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={state.columnSpacing}
                    onChange={(e) => updateState({ columnSpacing: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ columnSpacing: 100 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={state.columnSpacing}
                    onChange={(e) => updateState({ columnSpacing: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                  <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>
                    Column Spacing Scale
                  </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                  <input
                    type="range"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={state.spacingScaleX}
                    onChange={(e) => updateState({ spacingScaleX: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ spacingScaleX: 1 })}
                    style={{ flex: 1, minWidth: 0, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0.1"
                    max="10"
                      step="0.1"
                      value={state.spacingScaleX}
                      onChange={(e) => updateState({ spacingScaleX: Number(e.target.value) })}
                      style={{
                        width: '60px',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(31, 31, 46, 0.8)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '4px',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                  <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>
                    Row Spacing Scale
                  </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                  <input
                    type="range"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={state.spacingScaleY}
                    onChange={(e) => updateState({ spacingScaleY: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ spacingScaleY: 1 })}
                    style={{ flex: 1, minWidth: 0, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0.1"
                      max="10"
                      step="0.1"
                      value={state.spacingScaleY}
                      onChange={(e) => updateState({ spacingScaleY: Number(e.target.value) })}
                      style={{
                        width: '60px',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(31, 31, 46, 0.8)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '4px',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Text Effect */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text Effect</label>
              <select
                value={textEffect}
                onChange={(e) => setTextEffect(e.target.value as TextEffect)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.625rem',
                  background: 'rgba(31, 31, 46, 0.8)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              >
                <option value="none">None</option>
                <option value="arch-up">Arch Up</option>
                <option value="arch-down">Arch Down</option>
                <option value="wave">Wave</option>
                <option value="circle">Circle</option>
              </select>
            </div>

            {textEffect !== 'none' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Effect Intensity</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    value={effectIntensity}
                    onChange={(e) => setEffectIntensity(Number(e.target.value))}
                    onDoubleClick={() => setEffectIntensity(50)}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={effectIntensity}
                    onChange={(e) => setEffectIntensity(Number(e.target.value))}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ height: '1px', background: 'rgba(249, 115, 22, 0.2)', margin: '0.25rem 0' }} />

            {/* Color Presets */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <div onClick={() => setPresetsExpanded(!presetsExpanded)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{presetsExpanded ? '▼' : '▶'}</span>
                  Color Presets
                </label>
              </div>
              {presetsExpanded && (
                <>
                  <div style={{ display: 'flex', gap: '0.4rem', margin: '0.4rem 0' }}>
                    {(['text', 'background', 'stroke'] as const).map(target => (
                      <button
                        key={target}
                        onClick={() => setPresetTarget(target)}
                        style={{
                          flex: 1,
                          padding: '0.4rem',
                          background: presetTarget === target ? 'linear-gradient(135deg, #F97316, #EA580C)' : 'rgba(31, 31, 46, 0.8)',
                          border: presetTarget === target ? '1px solid #F97316' : '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: presetTarget === target ? 'white' : '#9ca3af',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textTransform: 'capitalize'
                        }}
                      >
                        {target}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
                    {COLOR_PRESETS.map(preset => (
                      <div key={preset.name} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                          {preset.name}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          {preset.colors.map(color => (
                            <button
                              key={color}
                              onClick={() => {
                                if (presetTarget === 'text') {
                                  updateState({ color });
                                } else if (presetTarget === 'background') {
                                  updateState({ backgroundColor: color });
                                } else if (presetTarget === 'stroke') {
                                  updateState({ strokeColor: color });
                                }
                              }}
                              style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: color,
                                border: '2px solid rgba(249, 115, 22, 0.3)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s'
                              }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Color Pickers */}
            <ColorPickerField
              label="Text Color"
              color={state.color}
              pickerKey="text"
              onChange={(color) => updateState({ color })}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text Opacity</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(state.textOpacity * 100)}
                  onChange={(e) => updateState({ textOpacity: Number(e.target.value) / 100 })}
                  onDoubleClick={() => updateState({ textOpacity: 1 })}
                  style={{ flex: 1, accentColor: '#F97316' }}
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={Math.round(state.textOpacity * 100)}
                  onChange={(e) => updateState({ textOpacity: Number(e.target.value) / 100 })}
                  style={{
                    width: '60px',
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(31, 31, 46, 0.8)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <ColorPickerField
                label="Background Color"
                color={state.backgroundColor}
                pickerKey="background"
                onChange={(color) => updateState({ backgroundColor: color })}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', color: '#e5e7eb', fontSize: '0.8125rem' }}>
                <span>Use Gradient</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                  <input
                    type="checkbox"
                    checked={state.backgroundUseGradient}
                    onChange={(e) => updateState({ backgroundUseGradient: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>
              {state.backgroundUseGradient && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <ColorPickerField
                        label="Gradient Start"
                        color={state.backgroundGradientStart}
                        pickerKey="backgroundGradientStart"
                        onChange={(color) => updateState({ backgroundGradientStart: color })}
                        containerStyle={{ gap: '0.3rem' }}
                        labelStyle={{ color: '#9ca3af', fontSize: '0.75rem' }}
                        buttonStyle={{ marginTop: '0.25rem' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <ColorPickerField
                        label="Gradient End"
                        color={state.backgroundGradientEnd}
                        pickerKey="backgroundGradientEnd"
                        onChange={(color) => updateState({ backgroundGradientEnd: color })}
                        containerStyle={{ gap: '0.3rem' }}
                        labelStyle={{ color: '#9ca3af', fontSize: '0.75rem' }}
                        buttonStyle={{ marginTop: '0.25rem' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <label style={{ color: '#9ca3af', fontSize: '0.75rem', fontWeight: 500 }}>Gradient Angle</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={state.backgroundGradientAngle}
                        onChange={(e) => updateState({ backgroundGradientAngle: Number(e.target.value) })}
                        onDoubleClick={() => updateState({ backgroundGradientAngle: 45 })}
                        style={{ flex: 1, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="360"
                        value={state.backgroundGradientAngle}
                        onChange={(e) => updateState({ backgroundGradientAngle: Number(e.target.value) })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stroke Controls */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <ColorPickerField
                label="Stroke Color"
                color={state.strokeColor}
                pickerKey="stroke"
                onChange={(color) => updateState({ strokeColor: color })}
                containerStyle={{ minWidth: 0 }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Stroke Width</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={state.strokeWidth}
                    onChange={(e) => updateState({ strokeWidth: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ strokeWidth: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={state.strokeWidth}
                    onChange={(e) => updateState({ strokeWidth: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Text Shadow */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', padding: '0.75rem', background: 'rgba(31, 31, 46, 0.6)', border: '1px solid rgba(249, 115, 22, 0.2)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  onClick={() => toggleEffectPanel('shadow')}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{ color: '#e5e7eb', fontSize: '0.9rem' }}>{effectPanelsOpen.shadow ? '▼' : '▶'}</span>
                  <span style={{ color: '#e5e7eb', fontSize: '0.875rem', fontWeight: 600 }}>Text Shadow</span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={state.textShadowEnabled}
                    onChange={(e) => updateState({ textShadowEnabled: e.target.checked })}
                  />
                  Enable
                </label>
              </div>
              {effectPanelsOpen.shadow && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Color</span>
                    <input
                      type="color"
                      value={state.textShadowColor}
                      onChange={(e) => updateState({ textShadowColor: e.target.value })}
                      style={{
                        width: '100%',
                        height: '40px',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '6px',
                        background: 'transparent'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Blur</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        value={state.textShadowBlur}
                        onChange={(e) => updateState({ textShadowBlur: Number(e.target.value) })}
                        style={{ flex: 1, minWidth: 0, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={state.textShadowBlur}
                        onChange={(e) => updateState({ textShadowBlur: Number(e.target.value) })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Offset X</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={state.textShadowOffsetX}
                        onChange={(e) => updateState({ textShadowOffsetX: Number(e.target.value) })}
                        onDoubleClick={() => updateState({ textShadowOffsetX: 0 })}
                        style={{ flex: 1, minWidth: 0, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="-100"
                        max="100"
                        value={state.textShadowOffsetX}
                        onChange={(e) => updateState({ textShadowOffsetX: Number(e.target.value) })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Offset Y</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={state.textShadowOffsetY}
                        onChange={(e) => updateState({ textShadowOffsetY: Number(e.target.value) })}
                        onDoubleClick={() => updateState({ textShadowOffsetY: 0 })}
                        style={{ flex: 1, minWidth: 0, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="-100"
                        max="100"
                        value={state.textShadowOffsetY}
                        onChange={(e) => updateState({ textShadowOffsetY: Number(e.target.value) })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Gloss Effect */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', padding: '0.75rem', background: 'rgba(31, 31, 46, 0.6)', border: '1px solid rgba(249, 115, 22, 0.2)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  onClick={() => toggleEffectPanel('gloss')}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{ color: '#e5e7eb', fontSize: '0.9rem' }}>{effectPanelsOpen.gloss ? '▼' : '▶'}</span>
                  <span style={{ color: '#e5e7eb', fontSize: '0.875rem', fontWeight: 600 }}>Gloss Highlight</span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={state.glossEnabled}
                    onChange={(e) => updateState({ glossEnabled: e.target.checked })}
                  />
                  Enable
                </label>
              </div>
              {effectPanelsOpen.gloss && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={state.glossIntensity}
                    onChange={(e) => updateState({ glossIntensity: Number(e.target.value) })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={state.glossIntensity}
                    onChange={(e) => updateState({ glossIntensity: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Glass Effect */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', padding: '0.75rem', background: 'rgba(31, 31, 46, 0.6)', border: '1px solid rgba(249, 115, 22, 0.2)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  onClick={() => toggleEffectPanel('glass')}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{ color: '#e5e7eb', fontSize: '0.9rem' }}>{effectPanelsOpen.glass ? '▼' : '▶'}</span>
                  <span style={{ color: '#e5e7eb', fontSize: '0.875rem', fontWeight: 600 }}>Glass Effect</span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={state.glassEnabled}
                    onChange={(e) => updateState({ glassEnabled: e.target.checked })}
                  />
                  Enable
                </label>
              </div>
              {effectPanelsOpen.glass && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Opacity</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(state.glassOpacity * 100)}
                        onChange={(e) => updateState({ glassOpacity: Number(e.target.value) / 100 })}
                        style={{ flex: 1, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={Math.round(state.glassOpacity * 100)}
                        onChange={(e) => updateState({ glassOpacity: Number(e.target.value) / 100 })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ color: '#e5e7eb', fontSize: '0.75rem', fontWeight: 500 }}>Highlight</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(state.glassHighlight * 100)}
                        onChange={(e) => updateState({ glassHighlight: Number(e.target.value) / 100 })}
                        style={{ flex: 1, accentColor: '#F97316' }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={Math.round(state.glassHighlight * 100)}
                        onChange={(e) => updateState({ glassHighlight: Number(e.target.value) / 100 })}
                        style={{
                          width: '60px',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(31, 31, 46, 0.8)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ height: '1px', background: 'rgba(249, 115, 22, 0.2)', margin: '0.25rem 0' }} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text Wave X</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.horizontalWave}
                    onChange={(e) => updateState({ horizontalWave: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ horizontalWave: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.horizontalWave}
                    onChange={(e) => updateState({ horizontalWave: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text Wave Y</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.verticalWave}
                    onChange={(e) => updateState({ verticalWave: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ verticalWave: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.verticalWave}
                    onChange={(e) => updateState({ verticalWave: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Grid Wave X</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.gridWaveX}
                    onChange={(e) => updateState({ gridWaveX: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ gridWaveX: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.gridWaveX}
                    onChange={(e) => updateState({ gridWaveX: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Grid Wave Y</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.gridWaveY}
                    onChange={(e) => updateState({ gridWaveY: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ gridWaveY: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={state.gridWaveY}
                    onChange={(e) => updateState({ gridWaveY: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ height: '1px', background: 'rgba(249, 115, 22, 0.2)', margin: '0.25rem 0' }} />

            {/* Rotation & Transform Controls */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Rotation</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    value={state.rotation}
                    onChange={(e) => updateState({ rotation: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ rotation: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="-180"
                    max="180"
                    value={state.rotation}
                    onChange={(e) => updateState({ rotation: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Rotation Offset</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="-90"
                    max="90"
                    value={state.rotationOffset}
                    onChange={(e) => updateState({ rotationOffset: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ rotationOffset: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="-90"
                    max="90"
                    value={state.rotationOffset}
                    onChange={(e) => updateState({ rotationOffset: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Skew X</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    value={state.skewX}
                    onChange={(e) => updateState({ skewX: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ skewX: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="-100"
                    max="100"
                    value={state.skewX}
                    onChange={(e) => updateState({ skewX: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Skew Y</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    value={state.skewY}
                    onChange={(e) => updateState({ skewY: Number(e.target.value) })}
                    onDoubleClick={() => updateState({ skewY: 0 })}
                    style={{ flex: 1, accentColor: '#F97316' }}
                  />
                  <input
                    type="number"
                    min="-100"
                    max="100"
                    value={state.skewY}
                    onChange={(e) => updateState({ skewY: Number(e.target.value) })}
                    style={{
                      width: '60px',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(31, 31, 46, 0.8)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Zoom</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="range"
                  min="0.1"
                  max="10"
                  step="0.1"
                  value={state.zoom}
                  onChange={(e) => updateState({ zoom: Number(e.target.value) })}
                  onDoubleClick={() => updateState({ zoom: 1 })}
                  style={{ flex: 1, accentColor: '#F97316' }}
                />
                <input
                  type="number"
                  min="0.1"
                  max="10"
                  step="0.1"
                  value={state.zoom}
                  onChange={(e) => updateState({ zoom: Number(e.target.value) })}
                  style={{
                    width: '60px',
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(31, 31, 46, 0.8)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ height: '1px', background: 'rgba(249, 115, 22, 0.2)', margin: '0.25rem 0' }} />

            {/* Grid & Image Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                    style={{ accentColor: '#F97316' }}
                  />
                  Show Grid
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={gridSnap}
                    onChange={(e) => setGridSnap(e.target.checked)}
                    style={{ accentColor: '#F97316' }}
                  />
                  Snap to Grid
                </label>
              </div>

              {showGrid && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Grid Size</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="range"
                      min="20"
                      max="200"
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      onDoubleClick={() => setGridSize(50)}
                      style={{ flex: 1, accentColor: '#F97316' }}
                    />
                    <input
                      type="number"
                      min="20"
                      max="200"
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      style={{
                        width: '60px',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(31, 31, 46, 0.8)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '4px',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Background Image</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundImageUpload}
                    style={{ display: 'none' }}
                    id="bg-image-upload"
                  />
                  <label htmlFor="bg-image-upload" style={{
                    flex: 1,
                    padding: '0.5rem',
                    background: 'linear-gradient(135deg, #06B6D4, #0891B2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}>
                    {backgroundImage ? 'Change Background' : 'Upload Background'}
                  </label>
                  {backgroundImage && (
                    <button onClick={() => setBackgroundImage(null)} style={{
                      padding: '0.5rem 0.875rem',
                      background: 'rgba(239, 68, 68, 0.8)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ color: '#e5e7eb', fontSize: '0.8125rem', fontWeight: 500 }}>Text Image/Texture</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleTextImageUpload}
                    style={{ display: 'none' }}
                    id="text-image-upload"
                  />
                  <label htmlFor="text-image-upload" style={{
                    flex: 1,
                    padding: '0.5rem',
                    background: 'linear-gradient(135deg, #06B6D4, #0891B2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}>
                    {textImage ? 'Change Texture' : 'Upload Texture'}
                  </label>
                  {textImage && (
                    <button onClick={() => setTextImage(null)} style={{
                      padding: '0.5rem 0.875rem',
                      background: 'rgba(239, 68, 68, 0.8)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ height: '1px', background: 'rgba(249, 115, 22, 0.2)', margin: '0.25rem 0' }} />

            {/* Help Text */}
            <div style={{
              padding: '0.75rem',
              background: 'rgba(31, 31, 46, 0.8)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '8px',
              color: '#9ca3af',
              fontSize: '0.75rem',
              lineHeight: 1.5
            }}>
              <strong style={{ color: '#06B6D4' }}>Quick Tips:</strong><br />
              • Use mode buttons above canvas<br />
              • Text Wave sliders ripple letters; Grid Wave warps the layout<br />
              • Double-click most sliders to reset their default value<br />
              • Right-click to pan, scroll to zoom
            </div>

            {/* Reset Button */}
            <button onClick={() => setState({
              text: 'YOUR TEXT',
              fontFamily: 'Inter',
              fontWeight: '400',
              fontStyle: 'normal',
              fontSize: 80,
              letterSpacing: 0,
              color: '#ffffff',
              textOpacity: 1,
              backgroundColor: '#1a1a2e',
              backgroundUseGradient: false,
              backgroundGradientStart: '#1a1a2e',
              backgroundGradientEnd: '#0f172a',
              backgroundGradientAngle: 45,
              strokeColor: '#F97316',
              strokeWidth: 0,
              rows: 1,
              columns: 1,
              rowSpacing: 100,
              columnSpacing: 100,
              spacingScaleX: 1,
              spacingScaleY: 1,
              rotation: 0,
              rotationOffset: 0,
              horizontalWave: 0,
              verticalWave: 0,
              gridWaveX: 0,
              gridWaveY: 0,
              textShadowEnabled: false,
              textShadowColor: '#000000',
              textShadowBlur: 0,
              textShadowOffsetX: 0,
              textShadowOffsetY: 0,
              glossEnabled: false,
              glossIntensity: 40,
              glassEnabled: false,
              glassOpacity: 0.4,
              glassHighlight: 0.6,
              offsetX: 0,
              offsetY: 0,
              skewX: 0,
              skewY: 0,
              zoom: 1,
              performanceMode: false,
              useTextCaching: false,
            })} style={{
              width: '100%',
              padding: '0.5rem',
              background: 'rgba(31, 31, 46, 0.8)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              borderRadius: '8px',
              color: '#F97316',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxSizing: 'border-box'
            }}>
              Reset All
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
